import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon } from "@material-ui/icons";

import AssetUploaderManager from "../AssetUploader/UploadStep/AssetUploaderManager";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import Confirmation from "../Shared/Confirmation/Confirmation";
import BackupStep from "../AssetUploader/BackupStep/BackupStep";


import { useSelector, useDispatch } from "react-redux";
import { clearSelectedCreatives } from "../../actions/app/creatives";

import { insertCreativesNoAssignments } from "../../services/dcm/creatives";
import { fetchUserAdvertisers } from "../../services/dcm/advertisers";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "50%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

function shouldUploadBackup(creatives) {
  for (let creative of creatives) {
    if (creative.file && creative.file.type) {
      if (creative.file.type.includes("zip")) return true;
    }
  }

  return false;
}

function getSteps(stepOptions) {
  if (stepOptions.isH5) {
    return [
      "Upload creative(s)",
      "Upload backup creative(s)",
      "Generate name(s)",
      "Review"
    ];
  } else {
    return ["Upload creative(s)", "Generate name(s)", "Review"];
  }
}

const creativeTypes = ["DISPLAY"];

function getStepContent(stepIndex, props) {
  switch (stepIndex) {
    case 0:
      return (
        <div>
          <AssetUploaderManager
            isThumbnailEnabled={true}
            filetype={"image/*, .zip, video/*"}
            creativeTypes={creativeTypes}
            selectedCreatives={props.selectedCreatives}
            setSelectedCreatives={props.setSelectedCreatives}
            placements={props.placements}
            canChooseExisting={false}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    case 1:
      if (!props.isH5) {
        return (
          <div>
            <AssetNaming
              isClickEnabled={true}
              isDragDisabled={true}
              isThumbnailEnabled={true}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      } else {
        return (
          <div>
          <BackupStep
            isClickEnabled={true}
            isDragDisabled={true}
            selectedCreatives={props.selectedCreatives}
            setSelectedCreatives={props.setSelectedCreatives}
            isThumbnailEnabled={false}
            ref={(r) => (props.stepRef.current = r)}
            />
        </div>
      );
    }
    case 2:
      if (!props.isH5) {
        return (
          <div>
            <AssetReview
              isClickEnabled={false}
              isDragDisabled={true}
              isThumbnailEnabled={true}
              hasCampaignData={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      } else {
        return (
          <div>
            <AssetNaming
              isClickEnabled={true}
              isDragDisabled={true}
              isThumbnailEnabled={true}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      }
    case 3:
      return (
        <div>
          <AssetReview
            isClickEnabled={false}
            isDragDisabled={true}
            isThumbnailEnabled={true}
            hasCampaignData={false}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    default:
      return " ";
  }
}

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const [snackbarSeverity, setSnackbarSeverity] = useState('warning')
  let [userAdvertisers, setUserAdvertisers] = useState([]);
  let [traffickedCreatives, setTraffickedCreatives] = useState({});

  const creativesState = useSelector((state) => state.creatives);
  let isH5 = shouldUploadBackup(Object.values(creativesState.byId));
  const steps = getSteps({ isH5 });

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const stepRef = useRef();

  useEffect(() => {
    fetchUserAdvertisers()
      .then((advertisersResponse) => {
        if (advertisersResponse.length > 0) {
          setUserAdvertisers(advertisersResponse)
        } else {
          setSnackbarMessage('No user advertisers found linked to your account. Please contact the site administrator before proceeding');
          setSnackbarSeverity('error')
          setSnackbarOpen(true);
        }
      })
      .catch((err) => console.log(err))
  }, []);

  const handleNext = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return;
      }
    }

    dispatch(clearSelectedCreatives());
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    setSelectedCreatives([]);
    setActiveStep(step);
    dispatch(clearSelectedCreatives());
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  function isStepComplete(step) {
    return completed.has(step);
  }

  const handleTraffic = async () => {
    insertCreativesNoAssignments(
      Object.values(creativesState.byId),
      { advertiserIds: userAdvertisers },
      "DISPLAY"
    );

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const buttonProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
                {...buttonProps}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {activeStep === steps.length ? (
            <Confirmation
              handleResetParent={handleReset}
              isMultiAdvertiser={true}
            />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Creatives
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disableElevation
                    disabled={userAdvertisers.length < 1}
                  >
                    Next
                  </Button>
                )}
              </div>
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: creativesState.assignedPlacements,
                isH5,
                setActiveStep
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        // autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => {setSnackbarOpen(false); setSnackbarSeverity('warning')}}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
